import { Checkbox } from "@mui/material";
import { Button, Tooltip } from "components";
import { TableCell, TableRow } from "components/atoms/BasicTable";
import Skeleton from "components/atoms/layout/Skeleton/Skeleton";
import LeadScore from "components/atoms/LeadScore/LeadScore";
import React, { useId, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getLeadScore,
  getLeadStats,
  InterfacePixelLeadData,
} from "store/slices/insights";
import { setPreviousRoute } from "store/slices/ui.slice";
import { getBalance } from "store/slices/user.slice";
import { toastHelper } from "utils/helper";
import { useLazeApi } from "utils/hooks/useLazyApi";
import { messages } from "utils/message";
import { limitSeconds, showDate } from "utils/workWithData";
interface PulseRowProps extends InterfacePixelLeadData {
  id: string;
  currentParams: string;
  clientId: string;
  thresholdIcp: number;
  thresholdBehavior: number;
  selectedRows: string[];
  isLoading?: boolean;
  unselectedIds: string[];
  isAllSelected: boolean;
  scoreBehavior?: number;
  scoreIcp?: number;
  handleSelectRow: (id: string) => void;
  handleUnselectRow: (id: string) => void;
  handleRevealContactByID: (leadId: string) => Promise<void>;
}
export const PulseRow: React.FC<PulseRowProps> = (row) => {
  const {
    currentParams,
    id,
    clientId,
    scoreIcp,
    scoreBehavior,
    selectedRows,
    unselectedIds,
    isAllSelected,
    thresholdBehavior,
    thresholdIcp,
    handleSelectRow,
    handleUnselectRow,
    handleRevealContactByID,
  } = row;
  const dispatch = useAppDispatch();
  const [isRevealLoading, setRevealLoading] = useState(false);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const prepareUrlAndSavePreviousRoute = (
    id: string,
    currentParams: string
  ) => {
    dispatch(setPreviousRoute(location.pathname));
    return id ? `/lead/${id}?${currentParams}` : "";
  };

  const urlPrepared = prepareUrlAndSavePreviousRoute(id, currentParams);
  const isAllListSelected = isAllSelected;
  const isSelected = selectedRows.includes(row.id) || isAllListSelected;
  const isUnselected = unselectedIds.includes(row.id);
  const contactsRevealeds = useAppSelector(
    (state) => state.insights.contactsRevealed
  );

  const isChecked = isAllListSelected ? !isUnselected : isSelected;
  const toastId = useId();
  const getRevealedContact = useMemo(() => {
    return contactsRevealeds.find((item) => item.id === id);
  }, [contactsRevealeds, id]);
  const ref = useLazeApi({
    onIntersect: () => {
      getScore();
      getStats();
    },
  });

  const getStats = () => {
    setScoreLoading(true);
    if (!row.duration || !row.pageViews || !row.visits) {
      dispatch(getLeadStats({ leadId: id }))
        .unwrap()
        .finally(() => setStatsLoading(false));
    }
  };
  const getScore = async () => {
    setScoreLoading(true);
    try {
      await dispatch(getLeadScore({ leadId: id, clientId })).unwrap();
    } catch (error) {
      console.error("Failed to get lead score:", error);
    } finally {
      setScoreLoading(false);
    }
  };

  return (
    <TableRow
      ref={ref}
      className={`hover:bg-gray-700 ${isChecked && "bg-paper-400"}`}
    >
      <TableCell link={{ to: urlPrepared }} classNameComponent="px-2">
        <Checkbox
          onClick={(event) => {
            event.stopPropagation();
            if (isAllListSelected) {
              handleUnselectRow(row.id);
              return;
            }
            handleSelectRow(row.id);
          }}
          checked={isChecked}
          sx={{
            color: "var(--color-gray-600)",
            "&.Mui-checked": { color: "var(--color-primary-500)" },
            "&:hover": { backgroundColor: "transparent" },
          }}
        />
      </TableCell>
      <TableCell
        link={{ to: urlPrepared }}
        classNameComponent="px-2"
        className="px-0"
      >
        <Tooltip content="ICP">
          <div className="flex flex-row gap-2 items-center justify-center text-white">
            <LeadScore
              iconName="target"
              value={scoreIcp || 0}
              threshold={thresholdIcp}
              loading={scoreLoading}
            />
          </div>
        </Tooltip>
      </TableCell>
      <TableCell
        link={{ to: urlPrepared }}
        classNameComponent="px-2"
        className="px-0"
      >
        <Tooltip content="Behavior">
          <div className="flex flex-row gap-2 items-center justify-center text-white">
            <LeadScore
              iconName="behavior"
              value={scoreBehavior || 0}
              threshold={thresholdBehavior}
              loading={scoreLoading}
            />
          </div>
        </Tooltip>
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="truncate max-w-[160px]">
        {statsLoading ? (
          <Skeleton width={40} height={20} />
        ) : (
          row.leadName || "-"
        )}
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="truncate">
        {statsLoading ? (
          <Skeleton width={40} height={20} />
        ) : (
          row.orgName || "-"
        )}
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="!text-center">
        {statsLoading ? <Skeleton width={40} height={20} /> : row.visits || "-"}
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="!text-center">
        {statsLoading ? (
          <Skeleton width={40} height={20} />
        ) : (
          row.pageViews || "-"
        )}
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="!text-center">
        {statsLoading ? (
          <Skeleton width={40} height={20} />
        ) : (
          limitSeconds(row.duration || 0)
        )}
      </TableCell>
      <TableCell link={{ to: urlPrepared }} className="!text-center">
        {row.visitLast ? showDate(row.visitLast, true, false) : "-"}
      </TableCell>
      <TableCell link={{ to: urlPrepared }}>
        {!row.isContactRevealed ? (
          <Button
            iconName="credits"
            iconSize={15}
            size="xs"
            isProcessing={getRevealedContact?.isLoading}
            onClick={async (e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setRevealLoading(true);

              toastHelper({
                toastId,
                message: "Revealing contact",
                type: "info",
              });

              try {
                await handleRevealContactByID(row.id);
                toastHelper({
                  toastId,
                  message: messages.ENRICHMENT_CONTACTREVEAL_SUCCESS,
                  type: "success",
                });
              } catch (error) {
                console.error(error);
                toastHelper({
                  toastId,
                  message: "Failed to reveal contact. Please try again later.",
                  type: "error",
                });
              } finally {
                dispatch(getBalance());
                setRevealLoading(false);
              }
            }}
          >
            Reveal Contact
          </Button>
        ) : (
          <div className="badge bg-greendark-500 font-bold min-w-[35px] sm text-greenlight-300">
            Contact Revealed
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};
